import React, { useState } from 'react';

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (confirmationText: string) => void;
  isDeleting: boolean;
}

const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  isDeleting 
}) => {
  const [confirmationText, setConfirmationText] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (confirmationText.trim().toUpperCase() === 'DELETE') {
      onConfirm(confirmationText);
    } else {
      alert('Please type "DELETE" exactly to confirm.');
    }
  };

  const handleClose = () => {
    setConfirmationText('');
    onClose();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px',
      backdropFilter: 'blur(4px)'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '20px',
        padding: '24px',
        maxWidth: '400px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'slideUp 0.3s ease-out'
      }}>
        {/* Warning Icon */}
        <div style={{
          textAlign: 'center',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: '32px',
            boxShadow: '0 4px 12px rgba(238, 90, 111, 0.3)'
          }}>
            ⚠️
          </div>
          <h2 style={{
            margin: '0 0 8px 0',
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1a1a1a'
          }}>
            Delete Account
          </h2>
          <p style={{
            margin: '0',
            fontSize: '0.9rem',
            color: '#666',
            lineHeight: '1.5'
          }}>
            This action cannot be undone
          </p>
        </div>

        {/* Warning Message */}
        <div style={{
          background: '#fff5f5',
          border: '1px solid #ffcccc',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <p style={{
            margin: '0 0 12px 0',
            fontSize: '0.9rem',
            color: '#dc3545',
            fontWeight: '600'
          }}>
            All your data will be permanently deleted:
          </p>
          <ul style={{
            margin: '0',
            paddingLeft: '20px',
            color: '#666',
            fontSize: '0.85rem',
            lineHeight: '1.8'
          }}>
            <li>Learning progress</li>
            <li>Quiz scores</li>
            <li>Streaks and XP</li>
            <li>Profile information</li>
          </ul>
        </div>

        {/* Confirmation Input */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#333'
          }}>
            Type <strong style={{ color: '#dc3545' }}>"DELETE"</strong> to confirm:
          </label>
          <input
            type="text"
            value={confirmationText}
            onChange={(e) => setConfirmationText(e.target.value)}
            placeholder="Type DELETE here"
            disabled={isDeleting}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '1rem',
              border: '2px solid #e0e0e0',
              borderRadius: '12px',
              background: isDeleting ? '#f5f5f5' : 'white',
              color: '#1a1a1a',
              transition: 'all 0.3s ease',
              boxSizing: 'border-box'
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !isDeleting) {
                handleConfirm();
              }
            }}
            autoFocus
          />
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px'
        }}>
          <button
            onClick={handleClose}
            disabled={isDeleting}
            style={{
              flex: 1,
              padding: '14px',
              background: '#f0f0f0',
              color: '#333',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              opacity: isDeleting ? 0.6 : 1,
              transition: 'all 0.3s ease'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={isDeleting || confirmationText.trim().toUpperCase() !== 'DELETE'}
            style={{
              flex: 1,
              padding: '14px',
              background: confirmationText.trim().toUpperCase() === 'DELETE' && !isDeleting
                ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
                : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: (isDeleting || confirmationText.trim().toUpperCase() !== 'DELETE') ? 'not-allowed' : 'pointer',
              opacity: (isDeleting || confirmationText.trim().toUpperCase() !== 'DELETE') ? 0.6 : 1,
              transition: 'all 0.3s ease',
              boxShadow: confirmationText.trim().toUpperCase() === 'DELETE' && !isDeleting
                ? '0 4px 12px rgba(220, 53, 69, 0.3)'
                : 'none'
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default DeleteAccountModal;

